import React, { useState } from 'react';
import { Card, Badge, Button, Collapse, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DetailList from './DetailList';
import type { ClassroomSummary } from '../types/dashboard';

interface ClassroomSummaryCardProps {
  classroom: ClassroomSummary;
  nurseryTimezone: string;
}

const ClassroomSummaryCard: React.FC<ClassroomSummaryCardProps> = ({ classroom, nurseryTimezone }) => {
  const { t } = useTranslation('components', { keyPrefix: 'classroomSummaryCard' });
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  return (
    <Card className="h-100 classroom-summary-card">
      <Card.Body className="d-flex flex-column">
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">{classroom.classroom}</h6>
            <Badge bg="primary">
              {t('childrenCount', { count: classroom.totalChildrenCount })}
            </Badge>
          </div>
          
          <hr className="my-2" />
          
          <Row className="mb-3 gy-3">
            <Col xs={6} md>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10" style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-check-circle text-success" style={{ fontSize: '1.1rem' }}></i>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('present')}
                  </div>
                  <div className="fw-bold text-success" style={{ fontSize: '1.25rem', lineHeight: '1' }}>
                    {classroom.PresentCount}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={6} md>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10" style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-x-circle text-danger" style={{ fontSize: '1.1rem' }}></i>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('absent')}
                  </div>
                  <div className="fw-bold text-danger" style={{ fontSize: '1.25rem', lineHeight: '1' }}>
                    {classroom.AbsentCount}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={6} md>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10" style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-schedule-check text-warning" style={{ fontSize: '1.1rem' }}></i>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('vacation')}
                  </div>
                  <div className="fw-bold text-warning" style={{ fontSize: '1.25rem', lineHeight: '1' }}>
                    {classroom.VacationCount}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={6} md>
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10" style={{ width: '32px', height: '32px' }}>
                  <i className="bi bi-schedule-event" style={{ fontSize: '1.1rem', color: '#009688' }}></i>
                </div>
                <div>
                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {t('holiday')}
                  </div>
                  <div className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '1', color: '#009688' }}>
                    {classroom.HolidayCount || 0}
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <Collapse in={isExpanded}>
            <div className="mt-3">
              {classroom.PresentCount > 0 && (
                <div className="mb-4">
                  <h6 className="text-success mb-3">
                    <i className="bi bi-check-circle me-1"></i>
                    {t('presentChildren', { count: classroom.PresentCount })}
                  </h6>
                  <DetailList 
                    type="present" 
                    data={classroom.PresentList}
                    nurseryTimezone={nurseryTimezone}
                  />
                </div>
              )}

              {classroom.AbsentCount > 0 && (
                <div className="mb-4">
                  <h6 className="text-danger mb-3">
                    <i className="bi bi-x-circle me-1"></i>
                    {t('absentChildren', { count: classroom.AbsentCount })}
                  </h6>
                  <DetailList 
                    type="absent" 
                    data={classroom.AbsentList}
                    nurseryTimezone={nurseryTimezone}
                  />
                </div>
              )}

              {classroom.VacationCount > 0 && (
                <div className="mb-4">
                  <h6 className="text-warning mb-3">
                    <i className="bi bi-schedule-check me-1"></i>
                    {t('vacationChildren', { count: classroom.VacationCount })}
                  </h6>
                  <DetailList 
                    type="vacation" 
                    data={classroom.VacationList}
                    nurseryTimezone={nurseryTimezone}
                  />
                </div>
              )}

              {(classroom.HolidayCount || 0) > 0 && (
                <div className="mb-4">
                  <h6 className="mb-3" style={{ color: '#009688' }}>
                    <i className="bi bi-schedule-event me-1"></i>
                    {t('holidayChildren', { count: classroom.HolidayCount })}
                  </h6>
                  <DetailList 
                    type="holiday" 
                    data={classroom.HolidayList || []}
                    nurseryTimezone={nurseryTimezone}
                  />
                </div>
              )}
            </div>
          </Collapse>
        </div>

        {/* Button always at the bottom */}
        <div className="mt-auto">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={toggleExpanded}
            className="w-100"
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <>
                <i className="bi bi-chevron-up me-1" style={{ fontSize: '0.875rem' }}></i>
                {t('hideDetails')}
              </>
            ) : (
              <>
                <i className="bi bi-chevron-down me-1" style={{ fontSize: '0.875rem' }}></i>
                {t('showDetails')}
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ClassroomSummaryCard;
